import { useId, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  FormGroup,
  IconButton,
  Input,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  AccountCircle,
  Person,
  Visibility,
  VisibilityOff,
  VpnKeyRounded,
} from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ORGANISATION_USER_ROLES, ROLE_LABELS } from 'app/pages/Users/types';
import PasswordStrengthMeter from 'app/pages/ResetPassword/components/PasswordStrengthMeter';
import { PASSWORD_REGEX, PASSWORD_REQUIREMENT } from 'app/pages/ResetPassword/password-policy';

import useInvite from '../hooks/useInvite';
import useAcceptInvite from '../hooks/useAcceptInvite';
import type { Invite } from '../types';

const schema = yup
  .object({
    forename: yup.string().trim().required('First name is required'),
    surname: yup.string().trim().required('Last name is required'),
    password: yup
      .string()
      .required('Password is required')
      .matches(PASSWORD_REGEX, { message: PASSWORD_REQUIREMENT, excludeEmptyString: true }),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Please confirm your password'),
  })
  .required();

type InvitationFormValues = yup.InferType<typeof schema>;

type ClosedState = 'expired' | 'accepted' | 'notFound' | 'unavailable';

const CLOSED_COPY: Record<ClosedState, { title: string; body: string; action: string }> = {
  expired: {
    title: 'Invitation expired',
    body: 'This invitation link has expired. Please ask your organisation admin to send you a new one.',
    action: 'Back to login',
  },
  accepted: {
    title: 'Account already set up',
    body: 'This invitation has already been used and your account is ready. You can log in now.',
    action: 'Go to login',
  },
  notFound: {
    title: 'Invitation not found',
    body: "We couldn't find this invitation. The link may be incorrect or no longer valid.",
    action: 'Back to login',
  },
  unavailable: {
    title: 'Something went wrong',
    body: "We couldn't load your invitation right now. Please try again.",
    action: 'Try again',
  },
};

const listFormat = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

function describeInvite({ organisationName, roles, pharmacyNames }: Invite): string {
  const roleLabels = ORGANISATION_USER_ROLES.filter((role) => roles.includes(role)).map(
    (role) => ROLE_LABELS[role]
  );
  const asRoles = listFormat.format(roleLabels);
  const where = pharmacyNames.length ? ` at ${listFormat.format(pharmacyNames)}` : '';

  return `${organisationName} has invited you to join Healthera Connect as ${asRoles}${where}.`;
}

function InvitationForm(): ReactElement {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();

  const {
    data: invite,
    error: inviteError,
    isError: inviteHasErrored,
    isFetching: isFetchingInvite,
    isInitialLoading,
    refetch: refetchInvite,
  } = useInvite(inviteId);
  const { mutate: acceptInvite, isPending: isAccepting } = useAcceptInvite(inviteId ?? '');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<InvitationFormValues>({ resolver: yupResolver(schema) });

  const passwordValue = watch('password') ?? '';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Each input is described by the caption beneath it so screen readers announce the
  // validation message (or the password rule) together with the field.
  const captionIdPrefix = useId();
  const captionId = (field: keyof InvitationFormValues): string => `${captionIdPrefix}-${field}`;
  // The error caption is only rendered while the field is invalid, so only then is it referenced.
  const errorCaptionId = (field: keyof InvitationFormValues): string | undefined =>
    errors[field] ? captionId(field) : undefined;

  const revealAdornment = (field: string, visible: boolean, toggle: () => void): ReactElement => (
    <InputAdornment position="end">
      <IconButton
        aria-label={`${visible ? 'Hide' : 'Show'} ${field}`}
        onClick={toggle}
        edge="end"
        size="small"
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  function onSubmit({ forename, surname, password }: InvitationFormValues): void {
    if (!inviteId || isAccepting) return;

    acceptInvite({ forename, surname, password }, { onSuccess: () => navigate('/login') });
  }

  // Covers the first load and a retry after the invite failed to load.
  if (isInitialLoading || (inviteHasErrored && isFetchingInvite)) {
    return (
      <Box className="vtwo:m-auto vtwo:flex vtwo:flex-col vtwo:items-center">
        <CircularProgress />
        <Typography className="vtwo:mt-4">Checking your invitation…</Typography>
      </Box>
    );
  }

  const closedState = getClosedState(invite, inviteHasErrored, inviteError);

  if (closedState) {
    const copy = CLOSED_COPY[closedState];
    const onAction =
      closedState === 'unavailable' ? () => refetchInvite() : () => navigate('/login');

    return (
      <Box className="vtwo:m-auto vtwo:w-2/3 vtwo:max-w-160 vtwo:text-center">
        <Typography variant="h5" component="h1" className="vtwo:mb-4">
          {copy.title}
        </Typography>
        <Typography className="vtwo:mb-4">{copy.body}</Typography>
        <Button className="vtwo:w-full" variant="contained" onClick={onAction}>
          {copy.action}
        </Button>
      </Box>
    );
  }

  if (!invite) return <></>;

  return (
    <Box className="vtwo:m-auto vtwo:w-2/3 vtwo:max-w-160">
      <Typography variant="h5" component="h1" className="vtwo:text-center vtwo:mb-4">
        Set up your account
      </Typography>

      <Typography className="vtwo:mb-4 vtwo:text-center">{describeInvite(invite)}</Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup className="vtwo:flex vtwo:flex-col vtwo:gap-4">
          <Input
            className="vtwo:w-full"
            color="success"
            value={invite.email}
            readOnly
            inputProps={{ 'aria-label': 'Email' }}
            startAdornment={
              <InputAdornment position="start">
                <AccountCircle />
              </InputAdornment>
            }
          />
          <div>
            <Input
              className="vtwo:w-full"
              color="success"
              placeholder="First name"
              inputProps={{
                'aria-label': 'First name',
                'aria-describedby': errorCaptionId('forename'),
              }}
              {...(errors.forename?.message && { error: true })}
              {...register('forename')}
              startAdornment={
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              }
            />
            {errors.forename?.message && (
              <Typography id={captionId('forename')} variant="caption" color="error">
                {errors.forename.message}
              </Typography>
            )}
          </div>
          <div>
            <Input
              className="vtwo:w-full"
              color="success"
              placeholder="Last name"
              inputProps={{
                'aria-label': 'Last name',
                'aria-describedby': errorCaptionId('surname'),
              }}
              {...(errors.surname?.message && { error: true })}
              {...register('surname')}
              startAdornment={
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              }
            />
            {errors.surname?.message && (
              <Typography id={captionId('surname')} variant="caption" color="error">
                {errors.surname.message}
              </Typography>
            )}
          </div>
          <div>
            <Input
              className="vtwo:w-full"
              color="success"
              placeholder="Password"
              inputProps={{ 'aria-label': 'Password', 'aria-describedby': captionId('password') }}
              type={showPassword ? 'text' : 'password'}
              {...(errors.password?.message && { error: true })}
              {...register('password')}
              startAdornment={
                <InputAdornment position="start">
                  <VpnKeyRounded />
                </InputAdornment>
              }
              endAdornment={revealAdornment('password', showPassword, () =>
                setShowPassword((show) => !show)
              )}
            />
            <PasswordStrengthMeter password={passwordValue} />
            <Typography
              id={captionId('password')}
              variant="caption"
              color={errors.password?.message ? 'error' : 'textSecondary'}
              className="vtwo:block"
            >
              {errors.password?.message ?? PASSWORD_REQUIREMENT}
            </Typography>
          </div>
          <div>
            <Input
              className="vtwo:w-full"
              color="success"
              placeholder="Confirm password"
              inputProps={{
                'aria-label': 'Confirm password',
                'aria-describedby': errorCaptionId('confirmPassword'),
              }}
              type={showConfirmPassword ? 'text' : 'password'}
              {...(errors.confirmPassword?.message && { error: true })}
              {...register('confirmPassword')}
              startAdornment={
                <InputAdornment position="start">
                  <VpnKeyRounded />
                </InputAdornment>
              }
              endAdornment={revealAdornment('confirm password', showConfirmPassword, () =>
                setShowConfirmPassword((show) => !show)
              )}
            />
            {errors.confirmPassword?.message && (
              <Typography id={captionId('confirmPassword')} variant="caption" color="error">
                {errors.confirmPassword.message}
              </Typography>
            )}
          </div>

          <Button className="vtwo:w-full" variant="contained" type="submit" disabled={isAccepting}>
            {isAccepting && <CircularProgress size={20} className="vtwo:mr-2.5" />}Create account
          </Button>
        </FormGroup>
      </form>
    </Box>
  );
}

export default InvitationForm;

// Only a 404 means the invite does not exist; anything else (network, 5xx) is worth retrying.
function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

function getClosedState(
  invite: Invite | undefined,
  inviteHasErrored: boolean,
  inviteError: unknown
): ClosedState | null {
  if (inviteHasErrored) return isNotFoundError(inviteError) ? 'notFound' : 'unavailable';
  if (!invite) return null;
  if (invite.status === 'accepted') return 'accepted';
  if (invite.status === 'expired') return 'expired';

  return null;
}
